import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LoginTransitionProps {
  onComplete: () => void;
  userName?: string;
}

export default function LoginTransition({ onComplete, userName }: LoginTransitionProps) {
  const { t } = useTranslation('common');
  const [phase, setPhase] = useState<'enter' | 'verify' | 'complete' | 'sweep' | 'done'>('enter');
  const audioCtxRef = useRef<AudioContext | null>(null);

  const playSound = () => {
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      audioCtxRef.current = ctx;
      const now = ctx.currentTime;

      const master = ctx.createGain();
      master.gain.value = 0.5;
      master.connect(ctx.destination);

      const playTone = (
        freq: number, start: number, dur: number,
        type: 'sine' | 'square' | 'sawtooth' | 'triangle' = 'sine',
        gain = 0.15
      ) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.connect(g); g.connect(master);
        osc.type = type;
        osc.frequency.setValueAtTime(freq, now + start);
        g.gain.setValueAtTime(0, now + start);
        g.gain.linearRampToValueAtTime(gain, now + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.05);
      };

      // シンプルな確認音：低→中→高の3音
      playTone(440, 0.0, 0.18, 'sine', 0.12);
      playTone(554, 0.18, 0.18, 'sine', 0.12);
      playTone(659, 0.36, 0.4, 'sine', 0.15);
      // 最後に柔らかいコード
      playTone(659, 0.8, 0.6, 'sine', 0.1);
      playTone(784, 0.82, 0.6, 'sine', 0.08);
      playTone(988, 0.84, 0.6, 'sine', 0.06);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    playSound();
    const t1 = setTimeout(() => setPhase('verify'), 400);
    const t2 = setTimeout(() => setPhase('complete'), 1000);
    const t3 = setTimeout(() => setPhase('sweep'), 1800);
    const t4 = setTimeout(() => setPhase('done'), 2400);
    const t5 = setTimeout(() => onComplete(), 2700);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      clearTimeout(t4); clearTimeout(t5);
      audioCtxRef.current?.close().catch(() => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = phase === 'enter' ? 0 : phase === 'verify' ? 50 : 100;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: '#FAFAF9' }}
    >
      {/* 背景の細いグリッド線 */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* メインカード */}
      <div
        className="relative flex flex-col items-center px-6 w-full max-w-xs sm:max-w-sm"
        style={{
          opacity: phase === 'sweep' || phase === 'done' ? 0 : 1,
          transform: phase === 'sweep' || phase === 'done' ? 'translateY(-12px)' : 'translateY(0)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
        }}
      >
        {/* ロゴ */}
        <div
          className="flex items-center justify-center bg-white rounded-2xl mb-6 sm:mb-8"
          style={{
            width: 72,
            height: 72,
            opacity: phase === 'enter' ? 0 : 1,
            transform: phase === 'enter' ? 'scale(0.85)' : 'scale(1)',
            transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <img
            src="https://static.readdy.ai/image/310af9d506808f9a31b9fe6283e83b26/5a0223c22c87ad007bff1a2065ab36b4.png"
            alt="logo"
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </div>

        {/* テキスト */}
        <div
          className="text-center mb-6 sm:mb-8"
          style={{
            opacity: phase === 'enter' ? 0 : 1,
            transform: phase === 'enter' ? 'translateY(8px)' : 'translateY(0)',
            transition: 'opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s',
          }}
        >
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 tracking-wide mb-1">
            {phase === 'complete' || phase === 'sweep' || phase === 'done'
              ? t('transition.login_complete')
              : t('transition.login_verifying')}
          </h2>
          {userName && (
            <p className="text-sm text-gray-500 mt-1">
              {t('transition.login_welcome')}<span className="text-gray-700 font-medium">{userName}</span>{t('transition.login_welcome_suffix')}
            </p>
          )}
        </div>

        {/* プログレスバー */}
        <div
          className="w-full h-0.5 bg-gray-200 rounded-full overflow-hidden"
          style={{
            opacity: phase === 'enter' ? 0 : 1,
            transition: 'opacity 0.4s ease 0.2s',
          }}
        >
          <div
            className="h-full rounded-full bg-gray-800"
            style={{
              width: `${progress}%`,
              transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
            }}
          />
        </div>

        {/* ステータステキスト */}
        <p
          className="text-xs text-gray-400 mt-3 tracking-widest"
          style={{
            opacity: phase === 'enter' ? 0 : 1,
            transition: 'opacity 0.4s ease 0.3s',
            fontFamily: 'monospace',
          }}
        >
          {phase === 'complete' || phase === 'sweep' || phase === 'done'
            ? 'AUTHENTICATED'
            : 'VERIFYING...'}
        </p>
      </div>

      {/* スウィープカーテン（白） */}
      <div
        className="absolute inset-0 bg-white"
        style={{
          transform: phase === 'sweep' || phase === 'done' ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left center',
          transition: 'transform 0.5s cubic-bezier(0.76,0,0.24,1)',
        }}
      />
    </div>
  );
}
